import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { defaultExperience, experienceWhenShellAuthUnavailable } from '../config'
import { fetchContactAuth } from '../contactAuth'
import { publishShellAuth } from '../shellAuthBridge'
import { readDev2222ModeFromSession, writeDev2222ModeToSession } from '../dev2222Session'
import { DevFeature2222Bar } from '../DevFeature2222Bar'
import {
  readFeature2222Claim,
  resolveShellExperience,
  type Dev2222Mode,
  type Experience,
} from '../experience'
import { SHELL_APP_TITLE } from '../shellTitle'
import { loadExperienceApp } from './loadExperienceApp'

type Phase =
  | { kind: 'loading' }
  | { kind: 'booting'; experience: Experience }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready'
      experience: Experience
      jwtPayload: Record<string, unknown> | null
      notice?: string
    }

function format2222ClaimForUi(payload: Record<string, unknown> | null): string {
  const v = readFeature2222Claim(payload)
  if (v === undefined) return '—'
  const t = v.trim()
  if (t.length <= 40) return t
  return `${t.slice(0, 40)}…`
}

type UnifiedShellProps = {
  pathExperience?: Experience
}

const shellDevUi = import.meta.env.DEV

function showShellDevChrome(token: string | undefined): boolean {
  return shellDevUi && !!token?.trim()
}

export function UnifiedShell({ pathExperience }: UnifiedShellProps) {
  const { token } = useParams<{ token: string }>()
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const authRequestRef = useRef<string | null>(null)
  const [dev2222Mode, setDev2222Mode] = useState<Dev2222Mode>(() => readDev2222ModeFromSession())

  const persistDev2222Mode = useCallback((mode: Dev2222Mode) => {
    writeDev2222ModeToSession(mode)
    setDev2222Mode(mode)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadExperience = async (
      experience: Experience,
      ready: { jwtPayload: Record<string, unknown> | null; notice?: string },
    ) => {
      setPhase({ kind: 'booting', experience })
      try {
        await loadExperienceApp(experience)
        if (cancelled) return
        setPhase({
          kind: 'ready',
          experience,
          jwtPayload: ready.jwtPayload,
          notice: ready.notice,
        })
      } catch (e) {
        if (cancelled) return
        const message = e instanceof Error ? e.message : String(e)
        setPhase({ kind: 'error', message: `Failed to load ${experience}: ${message}` })
      }
    }

    if (!token?.trim()) {
      void loadExperience(experienceWhenShellAuthUnavailable(pathExperience), {
        jwtPayload: null,
        notice: 'Missing token in path',
      })
      return () => {
        cancelled = true
      }
    }

    const requestKey = `${token}:${pathExperience ?? ''}:${dev2222Mode}`
    if (authRequestRef.current === requestKey) return
    authRequestRef.current = requestKey

    void (async () => {
      const auth = await fetchContactAuth(token)
      if (cancelled) return
      if (auth.kind === 'error') {
        authRequestRef.current = null
        await loadExperience(experienceWhenShellAuthUnavailable(pathExperience), {
          jwtPayload: null,
          notice: auth.message,
        })
        return
      }
      publishShellAuth(auth.smartAuth, token, auth.jwtPayload)
      const experience = resolveShellExperience(
        auth.jwtPayload,
        pathExperience,
        dev2222Mode,
        defaultExperience,
      )
      await loadExperience(experience, { jwtPayload: auth.jwtPayload })
    })()

    return () => {
      cancelled = true
      authRequestRef.current = null
    }
  }, [token, pathExperience, dev2222Mode])

  if (phase.kind === 'loading' || phase.kind === 'booting') {
    if (!showShellDevChrome(token)) return null
    const label = phase.kind === 'booting' ? phase.experience : '…'
    return <div style={styles.pad}>Loading {label} experience…</div>
  }
  if (phase.kind === 'error') {
    return <div style={{ ...styles.pad, color: '#b00020' }}>{phase.message}</div>
  }

  if (!showShellDevChrome(token)) return null

  return (
    <div style={styles.pad}>
      <p style={{ marginBottom: 8 }}>
        <strong>{SHELL_APP_TITLE}</strong> → <strong>{phase.experience}</strong>
      </p>
      {phase.notice ? (
        <p style={styles.notice}>Shell auth unavailable; loaded {phase.experience}.</p>
      ) : null}
      <DevFeature2222Bar
        claimPreview={format2222ClaimForUi(phase.jwtPayload)}
        mode={dev2222Mode}
        onModeChange={persistDev2222Mode}
      />
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  pad: { padding: 16, fontFamily: 'system-ui, sans-serif' },
  notice: { color: '#8a5a00', fontSize: 13, marginBottom: 8 },
}
